import { useCallback, useSyncExternalStore } from 'react';
import type { ProjectResponseDto, SceneResponseDto } from 'trace-viewer/cms';

const STORAGE_KEY = 'trace-saved-items';

export interface SavedProject {
    project: ProjectResponseDto;
    /** Scenes fetched when the project was saved (may be stale) */
    scenes: SceneResponseDto[];
    /** ISO timestamp of when the project was saved / last accessed */
    savedAt: string;
}

export interface SavedScene {
    scene: SceneResponseDto;
    savedAt: string;
}

interface SavedItemsData {
    projects: SavedProject[];
    scenes: SavedScene[];
}

function read(): SavedItemsData {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (raw) return JSON.parse(raw) as SavedItemsData;
    } catch {
        // corrupt data — start fresh
    }
    return { projects: [], scenes: [] };
}

function write(data: SavedItemsData) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    // Notify subscribers
    listeners.forEach(l => l());
}

// useSyncExternalStore plumbing
const listeners = new Set<() => void>();
function subscribe(cb: () => void) {
    listeners.add(cb);
    return () => { listeners.delete(cb); };
}

let snapshot = read();
function getSnapshot(): SavedItemsData {
    // Re-read only when we know a write happened (listeners fired)
    // For external changes (other tabs), re-read on each call
    const fresh = read();
    if (JSON.stringify(fresh) !== JSON.stringify(snapshot)) {
        snapshot = fresh;
    }
    return snapshot;
}

export function useSavedItems() {
    const data = useSyncExternalStore(subscribe, getSnapshot);

    const saveProject = useCallback((project: ProjectResponseDto, scenes: SceneResponseDto[]) => {
        const current = read();
        const filtered = current.projects.filter(p => p.project.id !== project.id);
        const updated: SavedItemsData = {
            ...current,
            projects: [{ project, scenes, savedAt: new Date().toISOString() }, ...filtered],
        };
        snapshot = updated;
        write(updated);
    }, []);

    const saveScene = useCallback((scene: SceneResponseDto) => {
        const current = read();
        const filtered = current.scenes.filter(s => s.scene.id !== scene.id);
        const updated: SavedItemsData = {
            ...current,
            scenes: [{ scene, savedAt: new Date().toISOString() }, ...filtered],
        };
        snapshot = updated;
        write(updated);
    }, []);

    const removeProject = useCallback((projectId: string) => {
        const current = read();
        const updated: SavedItemsData = {
            ...current,
            projects: current.projects.filter(p => p.project.id !== projectId),
        };
        snapshot = updated;
        write(updated);
    }, []);

    const removeScene = useCallback((sceneId: string) => {
        const current = read();
        const updated: SavedItemsData = {
            ...current,
            scenes: current.scenes.filter(s => s.scene.id !== sceneId),
        };
        snapshot = updated;
        write(updated);
    }, []);

    return {
        savedProjects: data.projects,
        savedScenes: data.scenes,
        saveProject,
        saveScene,
        removeProject,
        removeScene,
    };
}
